import type { serializedAgentErrorSchema } from "@wags/agent-runtime/common.contract.ts";
import type { AgentPublicEvent } from "@wags/agent-runtime/event.contract.ts";
import type { AgentSubscribeOptions } from "@wags/agent-runtime/index.ts";
import {
    type AgentInput,
    type AgentReceipt,
    agentInputSchema,
} from "@wags/agent-runtime/input.contract.ts";
import {
    type AgentEventPage,
    type AgentEventReadInput,
    type AgentSnapshot,
    type AgentSnapshotInput,
    agentEventPageSchema,
    agentEventReadInputSchema,
    agentInputSubmissionResponseSchema,
    agentSnapshotInputSchema,
    agentSnapshotSchema,
} from "@wags/agent-runtime/session.contract.ts";
import {
    type AgentTurn,
    type AgentTurnResult,
    agentTurnSchema,
} from "@wags/agent-runtime/turn.contract.ts";
import type { z } from "zod";

import type IAgentAccessTokenProvider from "./agent-access-token-provider.interface.ts";
import type IAgentClientFetch from "./agent-client-fetch.interface.ts";
import type IAgentClientWebSocketFactory from "./agent-client-web-socket-factory.interface.ts";
import type AgentSessionClient from "./agent-session-client.interface.ts";
import type {
    AgentRequestOptions,
    AgentSubscribeInput,
    AgentWaitOptions,
} from "./agent-session-client.interface.ts";
import {
    type AgentSmoothStreamOptions,
    smoothAgentStream,
} from "./smooth-stream.ts";
import {
    type AgentServerStreamFrame,
    agentClientStreamFrameSchema,
    agentServerStreamFrameSchema,
    agentStreamFrameMaxBytes,
    agentTransportErrorResponseSchema,
    createStreamCapabilityRequestSchema,
    createStreamCapabilityResponseSchema,
} from "./transport.contract.ts";

const MAX_LONG_POLL_MS = 30_000;
const TURN_POLL_RETRY_BASE_MS = 500;
const TURN_POLL_RETRY_MAX_DELAY_MS = 5_000;
const TURN_POLL_RETRY_WINDOW_MS = 120_000;
const TRANSIENT_HTTP_STATUSES = new Set([502, 503, 504]);
const WEB_SOCKET_IDLE_TIMEOUT_MS = 30_000;
const TERMINAL_TURN_STATUSES = new Set([
    "completed",
    "failed",
    "cancelled",
    "interrupted",
]);

export interface AgentClientOptions {
    baseUrl: string;
    fetch?: IAgentClientFetch["fetch"];
    getAccessToken?: IAgentAccessTokenProvider["getAccessToken"];
    webSocketFactory?: IAgentClientWebSocketFactory | null;
    longPollMs?: number;
    webSocketReconnectAttempts?: number;
    webSocketReconnectDelayMs?: number;
    smoothStream?: AgentSmoothStreamOptions;
}

export class AgentClientTimeoutError extends Error {
    constructor(timeoutMs: number) {
        super(`Agent client operation timed out after ${timeoutMs}ms`);
        this.name = "AgentClientTimeoutError";
    }
}

export class AgentClientCursorGapError extends Error {
    readonly expectedCursor: number;
    readonly receivedCursor: number;

    constructor(expectedCursor: number, receivedCursor: number) {
        super(
            `Agent event cursor gap: expected ${expectedCursor}, received ${receivedCursor}`,
        );
        this.name = "AgentClientCursorGapError";
        this.expectedCursor = expectedCursor;
        this.receivedCursor = receivedCursor;
    }
}

export class AgentClientProtocolError extends Error {
    constructor(message: string, options?: ErrorOptions) {
        super(message, options);
        this.name = "AgentClientProtocolError";
    }
}

export class AgentClientHttpError extends Error {
    readonly status: number;
    readonly agentError: z.infer<typeof serializedAgentErrorSchema>;

    constructor(
        status: number,
        agentError: z.infer<typeof serializedAgentErrorSchema>,
    ) {
        super(agentError.message);
        this.name = "AgentClientHttpError";
        this.status = status;
        this.agentError = agentError;
    }
}

function operationSignal(options: AgentRequestOptions): {
    cleanup: () => void;
    signal?: AbortSignal;
} {
    const timeoutMs = options.timeoutMs;
    if (timeoutMs === undefined) {
        return { cleanup: () => undefined, signal: options.signal };
    }
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
        throw new RangeError("timeoutMs must be a positive finite number");
    }

    const controller = new AbortController();
    const abort = () => controller.abort(options.signal?.reason);
    if (options.signal?.aborted === true) {
        abort();
    } else {
        options.signal?.addEventListener("abort", abort, { once: true });
    }
    const timer = setTimeout(
        () => controller.abort(new AgentClientTimeoutError(timeoutMs)),
        timeoutMs,
    );
    return {
        cleanup: () => {
            clearTimeout(timer);
            options.signal?.removeEventListener("abort", abort);
        },
        signal: controller.signal,
    };
}

async function abortableDelay(
    delayMs: number,
    signal?: AbortSignal,
): Promise<void> {
    signal?.throwIfAborted();
    await new Promise<void>((resolve, reject) => {
        const finish = () => {
            signal?.removeEventListener("abort", abort);
            resolve();
        };
        const timer = setTimeout(finish, delayMs);
        const abort = () => {
            clearTimeout(timer);
            signal?.removeEventListener("abort", abort);
            reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
        };
        signal?.addEventListener("abort", abort, { once: true });
    });
}

function isTransientTurnPollError(error: unknown): boolean {
    if (error instanceof AgentClientHttpError) {
        return TRANSIENT_HTTP_STATUSES.has(error.status);
    }
    return error instanceof TypeError;
}

async function httpErrorFromResponse(
    response: Response,
): Promise<AgentClientHttpError> {
    let body: unknown;
    try {
        body = JSON.parse(await response.text()) as unknown;
    } catch {
        body = undefined;
    }
    const parsed = agentTransportErrorResponseSchema.safeParse(body);
    if (parsed.success) {
        return new AgentClientHttpError(response.status, parsed.data.error);
    }
    return new AgentClientHttpError(response.status, {
        code: TRANSIENT_HTTP_STATUSES.has(response.status)
            ? "UPSTREAM_UNAVAILABLE"
            : "UNEXPECTED_HTTP_RESPONSE",
        message: `Agent transport returned HTTP ${response.status} without a valid error body`,
        retryable:
            response.status === 429 ||
            TRANSIENT_HTTP_STATUSES.has(response.status),
    });
}

function websocketUrl(url: URL): string {
    const socketUrl = new URL(url);
    if (socketUrl.protocol === "http:") socketUrl.protocol = "ws:";
    if (socketUrl.protocol === "https:") socketUrl.protocol = "wss:";
    return socketUrl.toString();
}

function connectionNonce(): string {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
        "",
    );
}

function isTerminalTurn(turn: AgentTurn): turn is AgentTurnResult {
    return TERMINAL_TURN_STATUSES.has(turn.status);
}

export class AgentClient {
    readonly baseUrl: URL;
    readonly fetch: IAgentClientFetch["fetch"];
    readonly getAccessToken?: IAgentAccessTokenProvider["getAccessToken"];
    readonly webSocketFactory?: IAgentClientWebSocketFactory;
    readonly longPollMs: number;
    readonly webSocketReconnectAttempts: number;
    readonly webSocketReconnectDelayMs: number;
    readonly smoothStream?: AgentSmoothStreamOptions;

    constructor(options: AgentClientOptions) {
        this.baseUrl = new URL(
            options.baseUrl.endsWith("/")
                ? options.baseUrl
                : `${options.baseUrl}/`,
        );
        this.fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
        this.getAccessToken = options.getAccessToken;
        this.longPollMs = options.longPollMs ?? MAX_LONG_POLL_MS;
        this.webSocketReconnectAttempts =
            options.webSocketReconnectAttempts ?? 2;
        this.webSocketReconnectDelayMs =
            options.webSocketReconnectDelayMs ?? 250;

        if (
            !Number.isInteger(this.longPollMs) ||
            this.longPollMs < 0 ||
            this.longPollMs > MAX_LONG_POLL_MS
        ) {
            throw new RangeError(
                "longPollMs must be an integer from 0 to 30000",
            );
        }
        if (
            !Number.isInteger(this.webSocketReconnectAttempts) ||
            this.webSocketReconnectAttempts < 0
        ) {
            throw new RangeError(
                "webSocketReconnectAttempts must be a nonnegative integer",
            );
        }
        if (
            !Number.isFinite(this.webSocketReconnectDelayMs) ||
            this.webSocketReconnectDelayMs < 0
        ) {
            throw new RangeError(
                "webSocketReconnectDelayMs must be a nonnegative finite number",
            );
        }
        this.smoothStream = options.smoothStream;
        if (
            this.smoothStream?.delayMs !== undefined &&
            (!Number.isFinite(this.smoothStream.delayMs) ||
                this.smoothStream.delayMs < 0)
        ) {
            throw new RangeError(
                "smoothStream.delayMs must be a nonnegative finite number",
            );
        }

        if (options.webSocketFactory !== undefined) {
            this.webSocketFactory = options.webSocketFactory ?? undefined;
        } else if (typeof globalThis.WebSocket === "function") {
            this.webSocketFactory = {
                create: (url, protocols) =>
                    new globalThis.WebSocket(
                        url,
                        protocols ? [...protocols] : [],
                    ),
            };
        }
    }

    connect(sessionId: string): AgentSessionClient {
        if (sessionId.length === 0)
            throw new TypeError("sessionId is required");
        return new ConnectedAgentSession(this, sessionId);
    }

    url(path: string): URL {
        return new URL(path.replace(/^\//u, ""), this.baseUrl);
    }

    async request<T>(
        sessionId: string,
        url: URL,
        init: RequestInit,
        schema: z.ZodType<T>,
        signal?: AbortSignal,
    ): Promise<T> {
        for (let attempt = 0; attempt < 2; attempt += 1) {
            signal?.throwIfAborted();
            const token = await this.getAccessToken?.({
                sessionId,
                forceRefresh: attempt > 0,
                signal,
            });
            signal?.throwIfAborted();
            const headers = new Headers(init.headers);
            headers.set("accept", "application/json");
            if (init.body !== undefined) {
                headers.set("content-type", "application/json");
            }
            if (token !== undefined)
                headers.set("authorization", `Bearer ${token}`);

            const response = await this.fetch(url, {
                ...init,
                headers,
                signal,
            });
            if (
                response.status === 401 &&
                this.getAccessToken !== undefined &&
                attempt === 0
            ) {
                continue;
            }
            if (!response.ok) throw await httpErrorFromResponse(response);
            return schema.parse(await response.json());
        }
        throw new Error("Unreachable access-token retry state");
    }
}

interface SocketQueueItem {
    done?: true;
    error?: Error;
    frame?: AgentServerStreamFrame;
}

function parseAgentStreamFrame(data: unknown): AgentServerStreamFrame {
    if (typeof data !== "string") {
        throw new AgentClientProtocolError(
            "Agent stream frame must be JSON text",
        );
    }
    if (new TextEncoder().encode(data).byteLength > agentStreamFrameMaxBytes) {
        throw new AgentClientProtocolError(
            "Agent stream frame exceeds one megabyte",
        );
    }
    let json: unknown;
    try {
        json = JSON.parse(data);
    } catch (error) {
        throw new AgentClientProtocolError(
            "Agent stream frame is not valid JSON",
            { cause: error },
        );
    }
    const parsed = agentServerStreamFrameSchema.safeParse(json);
    if (!parsed.success) {
        throw new AgentClientProtocolError(
            "Agent stream frame failed contract validation",
            { cause: parsed.error },
        );
    }
    return parsed.data;
}

function takeSequentialEvents(
    page: AgentEventPage,
    cursor: number,
): { cursor: number; events: AgentPublicEvent[] } {
    const events: AgentPublicEvent[] = [];
    let nextCursor = cursor;
    for (const event of page.events) {
        if (event.cursor <= nextCursor) continue;
        if (event.cursor !== nextCursor + 1) {
            throw new AgentClientCursorGapError(nextCursor + 1, event.cursor);
        }
        nextCursor = event.cursor;
        events.push(event);
    }
    if (page.events.length === 0 && page.headCursor > nextCursor) {
        throw new AgentClientCursorGapError(nextCursor + 1, page.headCursor);
    }
    return { cursor: nextCursor, events };
}

function isTerminalSubscribeSocketError(
    error: unknown,
    signal: AbortSignal | undefined,
): boolean {
    return (
        error instanceof AgentClientCursorGapError ||
        error instanceof AgentClientProtocolError ||
        signal?.aborted === true
    );
}

function assertOpenedStreamFrame(
    frame: Extract<AgentServerStreamFrame, { type: "stream.opened" }>,
    sessionId: string,
    cursor: number,
): void {
    if (frame.sessionId !== sessionId) {
        throw new AgentClientProtocolError(
            "Agent stream opened for a different session",
        );
    }
    if (frame.acceptedAfterCursor > frame.headCursor) {
        throw new AgentClientProtocolError(
            "Agent stream accepted cursor is ahead of its head cursor",
        );
    }
    if (frame.headCursor < cursor) {
        throw new AgentClientProtocolError(
            "Agent stream head cursor is behind the client cursor",
        );
    }
    if (frame.acceptedAfterCursor > cursor) {
        throw new AgentClientCursorGapError(
            cursor + 1,
            frame.acceptedAfterCursor + 1,
        );
    }
}

class ConnectedAgentSession implements AgentSessionClient {
    readonly sessionId: string;
    private readonly client: AgentClient;

    constructor(client: AgentClient, sessionId: string) {
        this.client = client;
        this.sessionId = sessionId;
    }

    async ensureMessagingRegistration(
        options: AgentRequestOptions = {},
    ): Promise<void> {
        const operation = operationSignal(options);
        try {
            await this.client.request(
                this.sessionId,
                this.endpoint("messaging-registration"),
                { method: "POST" },
                agentSnapshotInputSchema.pick({}),
                operation.signal,
            );
        } finally {
            operation.cleanup();
        }
    }

    async submit(
        unparsedInput: AgentInput,
        options: AgentRequestOptions = {},
    ): Promise<AgentReceipt> {
        const input = agentInputSchema.parse(unparsedInput);
        const operation = operationSignal(options);
        try {
            const response = await this.client.request(
                this.sessionId,
                this.endpoint("inputs"),
                { method: "POST", body: JSON.stringify(input) },
                agentInputSubmissionResponseSchema,
                operation.signal,
            );
            return response.receipt;
        } finally {
            operation.cleanup();
        }
    }

    async snapshot(
        unparsedInput: AgentSnapshotInput = {},
        options: AgentRequestOptions = {},
    ): Promise<AgentSnapshot> {
        const input = agentSnapshotInputSchema.parse(unparsedInput);
        const url = this.endpoint("snapshot");
        if (input.messageLimit !== undefined) {
            url.searchParams.set("messageLimit", String(input.messageLimit));
        }
        if (input.beforeCursor !== undefined) {
            url.searchParams.set("beforeCursor", String(input.beforeCursor));
        }
        const operation = operationSignal(options);
        try {
            return await this.client.request(
                this.sessionId,
                url,
                { method: "GET" },
                agentSnapshotSchema,
                operation.signal,
            );
        } finally {
            operation.cleanup();
        }
    }

    async getTurn(
        turnId: string,
        options: AgentRequestOptions = {},
    ): Promise<AgentTurn> {
        const operation = operationSignal(options);
        try {
            return await this.getTurnWithWait(turnId, 0, operation.signal);
        } finally {
            operation.cleanup();
        }
    }

    async readEvents(
        unparsedInput: AgentEventReadInput = {},
        options: AgentRequestOptions = {},
    ): Promise<AgentEventPage> {
        const input = agentEventReadInputSchema.parse(unparsedInput);
        const operation = operationSignal(options);
        try {
            return await this.readEventsWithSignal(input, operation.signal);
        } finally {
            operation.cleanup();
        }
    }

    async waitForTurn(
        turnId: string,
        options: AgentWaitOptions = {},
    ): Promise<AgentTurnResult> {
        const operation = operationSignal(options);
        try {
            let turn = await this.pollTurn(turnId, 0, operation.signal);
            let eventCursor = Math.max(0, (turn.startedCursor ?? 1) - 1);
            while (!isTerminalTurn(turn)) {
                if (options.onEvent !== undefined) {
                    eventCursor = await this.dispatchTurnEvents(
                        turnId,
                        eventCursor,
                        options.onEvent,
                        operation.signal,
                    );
                }
                turn = await this.pollTurn(
                    turnId,
                    this.client.longPollMs,
                    operation.signal,
                );
            }
            if (options.onEvent !== undefined) {
                await this.dispatchTurnEvents(
                    turnId,
                    eventCursor,
                    options.onEvent,
                    operation.signal,
                );
            }
            return turn;
        } finally {
            operation.cleanup();
        }
    }

    async run(
        input: AgentInput,
        options: AgentWaitOptions = {},
    ): Promise<AgentTurnResult> {
        const operation = operationSignal(options);
        try {
            const receipt = await this.submit(input, {
                signal: operation.signal,
            });
            if (receipt.turnId === undefined) {
                throw new AgentClientProtocolError(
                    "Submitted input did not create or identify a turn",
                );
            }
            return await this.waitForTurn(receipt.turnId, {
                signal: operation.signal,
                onEvent: options.onEvent,
            });
        } finally {
            operation.cleanup();
        }
    }

    subscribe(
        input: AgentSubscribeInput = {},
        options: AgentSubscribeOptions = {},
    ): AsyncIterable<AgentPublicEvent> {
        const smooth = this.client.smoothStream;
        if (smooth === undefined) return this.subscribeRaw(input, options);
        const callerSignal =
            input.signal === undefined
                ? options.signal
                : options.signal === undefined
                  ? input.signal
                  : AbortSignal.any([input.signal, options.signal]);

        const teardown = new AbortController();
        const rawSignal =
            callerSignal === undefined
                ? teardown.signal
                : AbortSignal.any([callerSignal, teardown.signal]);
        const events = this.subscribeRaw(input, {
            ...options,
            signal: rawSignal,
        });
        const smoothed = smoothAgentStream(events, smooth, callerSignal);
        return {
            [Symbol.asyncIterator]: (): AsyncIterator<AgentPublicEvent> => {
                const iterator = smoothed[Symbol.asyncIterator]();
                return {
                    next: () => iterator.next(),
                    return: async () => {
                        teardown.abort();
                        await iterator.return?.(undefined);
                        return { done: true, value: undefined };
                    },
                    throw: async (error: unknown) => {
                        teardown.abort();
                        await iterator.return?.(undefined);
                        throw error;
                    },
                };
            },
        };
    }

    private async *subscribeRaw(
        input: AgentSubscribeInput = {},
        options: AgentSubscribeOptions = {},
    ): AsyncIterable<AgentPublicEvent> {
        const parsedInput = agentEventReadInputSchema
            .pick({ afterCursor: true })
            .parse({ afterCursor: input.afterCursor });
        const signal =
            input.signal === undefined
                ? options.signal
                : options.signal === undefined
                  ? input.signal
                  : AbortSignal.any([input.signal, options.signal]);
        const operation = operationSignal({
            signal,
            timeoutMs: input.timeoutMs,
        });
        let cursor = parsedInput.afterCursor ?? 0;
        try {
            let consecutiveSocketFailures = 0;
            while (this.client.webSocketFactory !== undefined) {
                let opened = false;
                try {
                    for await (const item of this.watchSocket(
                        cursor,
                        operation.signal,
                        () => {
                            opened = true;
                        },
                    )) {
                        cursor = item.cursor;
                        yield item;
                    }
                } catch (error) {
                    if (
                        isTerminalSubscribeSocketError(error, operation.signal)
                    ) {
                        throw error;
                    }
                    if (error instanceof AgentClientTimeoutError) break;
                }
                if (opened) {
                    consecutiveSocketFailures = 0;
                } else {
                    consecutiveSocketFailures += 1;
                    if (
                        consecutiveSocketFailures >
                        this.client.webSocketReconnectAttempts
                    ) {
                        break;
                    }
                }
                await this.reconnectDelay(operation.signal);
            }

            while (true) {
                operation.signal?.throwIfAborted();
                const page = await this.readEventsWithSignal(
                    {
                        afterCursor: cursor,
                        limit: 500,
                        waitMs: this.client.longPollMs,
                    },
                    operation.signal,
                );
                const taken = takeSequentialEvents(page, cursor);
                cursor = taken.cursor;
                yield* taken.events;
            }
        } finally {
            operation.cleanup();
        }
    }

    private endpoint(suffix: string): URL {
        return this.client.url(
            `v1/agent-sessions/${encodeURIComponent(this.sessionId)}/${suffix}`,
        );
    }

    private async pollTurn(
        turnId: string,
        waitMs: number,
        signal?: AbortSignal,
    ): Promise<AgentTurn> {
        const streakStartedAt = Date.now();
        let delayMs = TURN_POLL_RETRY_BASE_MS;
        while (true) {
            try {
                return await this.getTurnWithWait(turnId, waitMs, signal);
            } catch (error) {
                if (
                    signal?.aborted === true ||
                    !isTransientTurnPollError(error) ||
                    Date.now() - streakStartedAt + delayMs >
                        TURN_POLL_RETRY_WINDOW_MS
                ) {
                    throw error;
                }
                await abortableDelay(delayMs, signal);
                delayMs = Math.min(delayMs * 2, TURN_POLL_RETRY_MAX_DELAY_MS);
            }
        }
    }

    private async getTurnWithWait(
        turnId: string,
        waitMs: number,
        signal?: AbortSignal,
    ): Promise<AgentTurn> {
        const boundedWait =
            agentEventReadInputSchema.parse({ waitMs }).waitMs ?? 0;
        const url = this.endpoint(`turns/${encodeURIComponent(turnId)}`);
        if (boundedWait > 0)
            url.searchParams.set("waitMs", String(boundedWait));
        return await this.client.request(
            this.sessionId,
            url,
            { method: "GET" },
            agentTurnSchema,
            signal,
        );
    }

    private async readEventsWithSignal(
        unparsedInput: AgentEventReadInput,
        signal?: AbortSignal,
    ): Promise<AgentEventPage> {
        const input = agentEventReadInputSchema.parse(unparsedInput);
        const url = this.endpoint("events");
        if (input.afterCursor !== undefined) {
            url.searchParams.set("afterCursor", String(input.afterCursor));
        }
        if (input.limit !== undefined) {
            url.searchParams.set("limit", String(input.limit));
        }
        if (input.waitMs !== undefined) {
            url.searchParams.set("waitMs", String(input.waitMs));
        }
        const page = await this.client.request(
            this.sessionId,
            url,
            { method: "GET" },
            agentEventPageSchema,
            signal,
        );
        for (const event of page.events) {
            if (event.sessionId !== this.sessionId) {
                throw new AgentClientProtocolError(
                    "Agent event belongs to a different session",
                );
            }
        }
        return page;
    }

    private async dispatchTurnEvents(
        turnId: string,
        initialCursor: number,
        onEvent: (event: AgentPublicEvent) => void,
        signal?: AbortSignal,
    ): Promise<number> {
        let cursor = initialCursor;
        let hasMore: boolean;
        do {
            const page = await this.readEventsWithSignal(
                { afterCursor: cursor, limit: 500, waitMs: 0 },
                signal,
            );
            const taken = takeSequentialEvents(page, cursor);
            cursor = taken.cursor;
            for (const event of taken.events) {
                if (event.turnId === turnId) onEvent(event);
            }
            hasMore = page.hasMore;
        } while (hasMore);
        return cursor;
    }

    private async *watchSocket(
        afterCursor: number,
        signal: AbortSignal | undefined,
        onOpened: () => void,
    ): AsyncIterable<AgentPublicEvent> {
        const factory = this.client.webSocketFactory;
        if (factory === undefined) return;

        const capabilityRequest = createStreamCapabilityRequestSchema.parse({
            sessionId: this.sessionId,
            permissions: ["events"],
            afterCursor,
            connectionNonce: connectionNonce(),
        });
        const capability = await this.client.request(
            this.sessionId,
            this.endpoint("stream-capabilities"),
            { method: "POST", body: JSON.stringify(capabilityRequest) },
            createStreamCapabilityResponseSchema,
            signal,
        );
        const socket = factory.create(
            websocketUrl(this.endpoint("events/stream")),
            [capability.protocol, capability.capability],
        );
        const queue: SocketQueueItem[] = [];
        let wake: (() => void) | undefined;
        let opened = false;
        let cursor = afterCursor;
        const push = (item: SocketQueueItem) => {
            queue.push(item);
            wake?.();
            wake = undefined;
        };
        let idleTimeout: ReturnType<typeof setTimeout> | undefined;
        const resetIdleTimeout = () => {
            clearTimeout(idleTimeout);
            idleTimeout = setTimeout(() => {
                push({
                    error: new AgentClientTimeoutError(
                        WEB_SOCKET_IDLE_TIMEOUT_MS,
                    ),
                });
            }, WEB_SOCKET_IDLE_TIMEOUT_MS);
        };
        const handleMessage = (event: MessageEvent<unknown>) => {
            try {
                resetIdleTimeout();
                push({ frame: parseAgentStreamFrame(event.data) });
            } catch (error) {
                push({
                    error:
                        error instanceof Error
                            ? error
                            : new Error(String(error)),
                });
            }
        };
        const handleClose = () => push({ done: true });
        const handleError = () => push({ done: true });
        const handleAbort = () => {
            socket.close(1000, "Client operation aborted");
            push({
                error:
                    signal?.reason ?? new DOMException("Aborted", "AbortError"),
            });
        };
        socket.addEventListener("message", handleMessage);
        socket.addEventListener("close", handleClose);
        socket.addEventListener("error", handleError);
        signal?.addEventListener("abort", handleAbort, { once: true });

        try {
            resetIdleTimeout();
            while (true) {
                if (queue.length === 0) {
                    await new Promise<void>((resolve) => {
                        wake = resolve;
                    });
                }
                const item = queue.shift();
                if (item === undefined) continue;
                if (item.error !== undefined) throw item.error;
                if (item.done === true) return;
                const frame = item.frame;
                if (frame === undefined) continue;
                if (frame.type === "stream.opened") {
                    if (opened) {
                        throw new AgentClientProtocolError(
                            "Agent stream opened more than once",
                        );
                    }
                    assertOpenedStreamFrame(frame, this.sessionId, cursor);
                    opened = true;
                    onOpened();
                    continue;
                }
                if (!opened) {
                    throw new AgentClientProtocolError(
                        "Agent stream sent data before stream.opened",
                    );
                }
                if (frame.type === "stream.error") {
                    if (frame.terminal) {
                        throw new AgentClientProtocolError(
                            `Agent stream terminated: ${frame.error.message}`,
                        );
                    }
                    return;
                }
                for (const event of frame.events) {
                    if (event.sessionId !== this.sessionId) {
                        throw new AgentClientProtocolError(
                            "Agent event belongs to a different session",
                        );
                    }
                    if (event.cursor <= cursor) continue;
                    if (event.cursor !== cursor + 1) {
                        throw new AgentClientCursorGapError(
                            cursor + 1,
                            event.cursor,
                        );
                    }
                    cursor = event.cursor;
                    yield event;
                }
                socket.send(
                    JSON.stringify(
                        agentClientStreamFrameSchema.parse({
                            type: "stream.ack",
                            cursor,
                        }),
                    ),
                );
            }
        } finally {
            clearTimeout(idleTimeout);
            socket.removeEventListener("message", handleMessage);
            socket.removeEventListener("close", handleClose);
            socket.removeEventListener("error", handleError);
            signal?.removeEventListener("abort", handleAbort);
            socket.close(1000, "Client stream closed");
        }
    }

    private async reconnectDelay(signal?: AbortSignal): Promise<void> {
        if (this.client.webSocketReconnectDelayMs === 0) return;
        await abortableDelay(this.client.webSocketReconnectDelayMs, signal);
    }
}
